import React, { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getInvoiceReminderSettings,
  updateInvoiceReminderSettings,
  runInvoiceReminders,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { BellRinging, Clock, Lightning, FloppyDisk, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const DEFAULTS = {
  enabled: false,
  hour: 9,
  minute: 0,
  min_days_between: 3,
  max_reminders: 5,
  send_first_notice_on_due_date: true,
};

export default function InvoiceRemindersSettingsPage() {
  const { t } = useLanguage();
  const isSpanish = (t('language') || '').toLowerCase() !== 'english';
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const fetchSettings = async () => {
    try {
      const { data } = await getInvoiceReminderSettings();
      setSettings({ ...DEFAULTS, ...(data || {}) });
    } catch (error) {
      toast.error(
        isSpanish
          ? 'No se pudieron cargar los ajustes de recordatorios'
          : 'Failed to load reminder settings',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!settings.enabled,
        hour: Math.max(0, Math.min(23, Number(settings.hour) || 0)),
        minute: Math.max(0, Math.min(59, Number(settings.minute) || 0)),
        min_days_between: Math.max(1, Number(settings.min_days_between) || 1),
        max_reminders: Math.max(1, Number(settings.max_reminders) || 1),
        send_first_notice_on_due_date: !!settings.send_first_notice_on_due_date,
      };
      const { data } = await updateInvoiceReminderSettings(payload);
      setSettings({ ...DEFAULTS, ...(data || payload) });
      toast.success(isSpanish ? 'Ajustes guardados' : 'Settings saved');
    } catch (error) {
      toast.error(
        error.response?.data?.detail || (isSpanish ? 'Error al guardar' : 'Failed to save'),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const { data } = await runInvoiceReminders(true);
      const sent = data?.sent ?? data?.emails_sent ?? 0;
      const skipped = data?.skipped ?? 0;
      toast.success(
        isSpanish
          ? `Ejecución completada · ${sent} enviadas, ${skipped} omitidas`
          : `Run completed · ${sent} sent, ${skipped} skipped`,
      );
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          (isSpanish ? 'Error al ejecutar' : 'Failed to run reminders'),
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <Layout title={isSpanish ? 'Recordatorios de Facturas' : 'Invoice Reminders'}>
      <div className="max-w-3xl space-y-6" data-testid="invoice-reminders-page">
        <div className="valhalla-card p-6 bg-gradient-to-br from-[#E2B05E]/10 to-transparent border-[#E2B05E]/30">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-[#E2B05E]/20 text-[#E2B05E]">
              <BellRinging weight="duotone" className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h2 className="font-['Outfit'] text-xl font-bold text-[#F3F4F6]">
                {isSpanish
                  ? 'Recordatorios automáticos de cobranza'
                  : 'Automatic collection reminders'}
              </h2>
              <p className="text-sm text-[#9CA3AF] mt-1 leading-relaxed">
                {isSpanish
                  ? 'Envía correos automáticos a clientes con facturas vencidas, adjuntando el PDF. Configura el horario del job diario, cuántos recordatorios por factura y el espaciado mínimo entre ellos.'
                  : 'Sends automatic emails to clients with overdue invoices, attaching the PDF. Configure the daily job time, number of reminders per invoice, and minimum spacing.'}
              </p>
            </div>
          </div>
        </div>

        {/* Main toggle */}
        <div className="valhalla-card p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-[#F3F4F6]">
                {isSpanish ? 'Activar recordatorios' : 'Enable reminders'}
              </h3>
              <p className="text-sm text-[#9CA3AF] mt-1">
                {isSpanish
                  ? 'Cuando está activado, el job se ejecuta diariamente a la hora configurada.'
                  : 'When enabled, the job runs daily at the configured time.'}
              </p>
            </div>
            <Switch
              checked={settings.enabled}
              disabled={loading}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: v }))}
              data-testid="reminders-enabled-switch"
            />
          </div>
        </div>

        {/* Schedule */}
        <div className="valhalla-card p-5 space-y-4">
          <div className="flex items-center gap-2 text-[#F3F4F6]">
            <Clock weight="duotone" className="w-5 h-5 text-[#3AB8D6]" />
            <h3 className="font-semibold">{isSpanish ? 'Horario diario (UTC)' : 'Daily schedule (UTC)'}</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">{isSpanish ? 'Hora' : 'Hour'} (0-23)</Label>
              <Input
                type="number"
                min={0}
                max={23}
                value={settings.hour}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, hour: parseInt(e.target.value, 10) || 0 }))
                }
                className="valhalla-input"
                disabled={loading}
                data-testid="reminders-hour-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">{isSpanish ? 'Minuto' : 'Minute'} (0-59)</Label>
              <Input
                type="number"
                min={0}
                max={59}
                value={settings.minute}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, minute: parseInt(e.target.value, 10) || 0 }))
                }
                className="valhalla-input"
                disabled={loading}
                data-testid="reminders-minute-input"
              />
            </div>
          </div>
        </div>

        {/* Policy */}
        <div className="valhalla-card p-5 space-y-4">
          <div className="flex items-center gap-2 text-[#F3F4F6]">
            <Warning weight="duotone" className="w-5 h-5 text-[#E2B05E]" />
            <h3 className="font-semibold">
              {isSpanish ? 'Política de envío (anti-spam)' : 'Sending policy (anti-spam)'}
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">
                {isSpanish ? 'Días mínimos entre recordatorios' : 'Min days between reminders'}
              </Label>
              <Input
                type="number"
                min={1}
                value={settings.min_days_between}
                onChange={(e) =>
                  setSettings((s) => ({
                    ...s,
                    min_days_between: parseInt(e.target.value, 10) || 1,
                  }))
                }
                className="valhalla-input"
                disabled={loading}
                data-testid="reminders-min-days-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">
                {isSpanish ? 'Máximo por factura' : 'Max per invoice'}
              </Label>
              <Input
                type="number"
                min={1}
                value={settings.max_reminders}
                onChange={(e) =>
                  setSettings((s) => ({
                    ...s,
                    max_reminders: parseInt(e.target.value, 10) || 1,
                  }))
                }
                className="valhalla-input"
                disabled={loading}
                data-testid="reminders-max-input"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 pt-2">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-[#F3F4F6]">
                {isSpanish
                  ? 'Enviar primer aviso el día de vencimiento'
                  : 'Send first notice on due date'}
              </h4>
              <p className="text-xs text-[#9CA3AF] mt-1">
                {isSpanish
                  ? 'Si está desactivado, solo envía cuando la factura ya está vencida (due_date < hoy).'
                  : 'If disabled, only sends after invoices are past due (due_date < today).'}
              </p>
            </div>
            <Switch
              checked={settings.send_first_notice_on_due_date}
              disabled={loading}
              onCheckedChange={(v) =>
                setSettings((s) => ({ ...s, send_first_notice_on_due_date: v }))
              }
              data-testid="reminders-first-notice-switch"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleRunNow}
            disabled={loading || running}
            className="border-[#3AB8D6]/40 text-[#3AB8D6] hover:bg-[#3AB8D6]/10"
            data-testid="reminders-run-now-btn"
          >
            <Lightning className="w-4 h-4 mr-2" weight="duotone" />
            {running
              ? isSpanish
                ? 'Ejecutando...'
                : 'Running...'
              : isSpanish
                ? 'Ejecutar ahora'
                : 'Run now'}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={loading || saving}
            className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14] font-semibold"
            data-testid="reminders-save-btn"
          >
            <FloppyDisk className="w-4 h-4 mr-2" weight="duotone" />
            {saving ? (isSpanish ? 'Guardando...' : 'Saving...') : isSpanish ? 'Guardar' : 'Save'}
          </Button>
        </div>

        <p className="text-xs text-[#6B7280] text-center">
          {isSpanish
            ? 'Tip: los recordatorios por cliente se controlan en la ficha de cada cliente (campo "Recibir recordatorios").'
            : 'Tip: per-client reminders are controlled on each client\'s profile.'}
        </p>
      </div>
    </Layout>
  );
}
